import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft, Loader2, Lock, PlayCircle, Clock, BookOpen, ListChecks, User,
} from "lucide-react";

export default function PublicCourse() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/courses/${id}`).then(({ data }) => setCourse(data)).catch(() => setError(true));
  }, [id]);

  useSeo(course ? {
    title: `${course.title} Course | Classroom to Corporate`,
    description: (course.description || "").slice(0, 160),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Course",
      name: course.title,
      description: course.description,
      url: window.location.href,
      educationalLevel: course.level,
      about: course.category,
      inLanguage: ["en", "hi", "gu"],
      provider: { "@type": "EducationalOrganization", name: "Classroom to Corporate", sameAs: window.location.origin },
      hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", instructor: course.instructor ? { "@type": "Person", name: course.instructor } : undefined },
    },
  } : {});

  const signIn = () => navigate("/login");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="public-course-brand">
            <img src="/logo.png" alt="Classroom to Corporate" className="h-9 w-9 object-contain" />
            <span className="font-heading text-lg font-bold text-slate-900 hidden sm:inline">Classroom to <span className="text-amber-600">Corporate</span></span>
          </button>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button onClick={signIn} className="bg-blue-600 hover:bg-blue-700" data-testid="public-course-signin">{t("browse.signIn")}</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-testid="public-course-page">
        <button
          onClick={() => navigate("/browse")}
          className="mb-6 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
          data-testid="public-course-back"
        >
          <ArrowLeft className="h-4 w-4" /> {t("publicCourse.back")}
        </button>

        {error ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
            <p className="text-slate-500">{t("publicCourse.notFound")}</p>
            <Button onClick={() => navigate("/browse")} className="mt-4 bg-blue-600 hover:bg-blue-700">{t("publicCourse.back")}</Button>
          </div>
        ) : !course ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_360px] gap-8">
            <div>
              <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-900 shadow-lg">
                <img src={course.thumbnail} alt="" className="h-full w-full object-cover opacity-60" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <PlayCircle className="h-16 w-16 opacity-90" />
                  <p className="mt-3 font-heading text-lg font-semibold px-6 text-center">{course.title}</p>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-blue-600">{course.category}</Badge>
                  <Badge variant="outline">{course.level}</Badge>
                </div>
                <h1 className="font-heading text-3xl font-extrabold text-slate-900">{course.title}</h1>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                  <User className="h-3.5 w-3.5" /> {t("courseDetail.by")} {course.instructor}
                </p>
                <p className="mt-4 text-slate-600 leading-relaxed">{course.description}</p>
              </div>

              <Card className="mt-8 border-slate-200 p-6" data-testid="public-syllabus">
                <div className="flex items-center gap-2 mb-1">
                  <ListChecks className="h-5 w-5 text-blue-600" />
                  <h3 className="font-heading text-xl font-bold text-slate-900">{t("publicCourse.syllabus")}</h3>
                </div>
                <p className="text-sm text-slate-500 mb-5">{t("publicCourse.previewNote")}</p>
                <ul className="space-y-1" data-testid="public-lesson-list">
                  {course.lessons.map((l, i) => (
                    <li
                      key={`${i}-${l.title}`}
                      data-testid="public-lesson-item"
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                        {i + 1}
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium text-slate-800">{l.title}</span>
                        {l.duration && (
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Clock className="h-3 w-3" /> {l.duration}
                          </span>
                        )}
                      </span>
                      <Lock className="h-4 w-4 shrink-0 text-slate-300" />
                    </li>
                  ))}
                  {course.lessons.length === 0 && (
                    <p className="text-sm text-slate-400 py-4 text-center">{t("publicCourse.noLessons")}</p>
                  )}
                </ul>
              </Card>
            </div>

            <aside className="lg:sticky lg:top-24 h-fit rounded-2xl border border-slate-200 bg-white p-6">
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-slate-500"><BookOpen className="h-4 w-4" /> {t("publicCourse.lessonsLabel")}</span>
                  <span className="font-semibold text-slate-900" data-testid="public-lesson-count">{course.lesson_count}</span>
                </div>
                {course.has_quiz && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-slate-500"><ListChecks className="h-4 w-4" /> {t("publicCourse.quizLabel")}</span>
                    <span className="font-semibold text-slate-900">{course.quiz_count}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-slate-500"><Clock className="h-4 w-4" /> {t("courses.progress")}</span>
                  <span className="font-semibold text-slate-900">{course.level}</span>
                </div>
              </div>
              <Button onClick={signIn} className="mt-5 w-full bg-blue-600 hover:bg-blue-700" data-testid="public-course-enroll-cta">
                <Lock className="mr-1.5 h-4 w-4" /> {t("browse.signInToEnroll")}
              </Button>
              <p className="mt-3 text-center text-xs text-slate-400">{t("publicCourse.enrollHint")}</p>
            </aside>
          </div>
        )}
      </main>
      <WhatsAppButton message={course ? t("whatsapp.courseMessage", { course: course.title }) : undefined} />
    </div>
  );
}
